import { logger } from '../../utils/logger.js'
import maplibregl, { addProtocol, removeProtocol } from 'maplibre-gl'
import { Capacitor } from '@capacitor/core'
import { fromUrl, type GeoTIFF, type GeoTIFFImage } from 'geotiff'
import SnowDepthWorker from './snow-depth-worker.ts?worker'
import { timelineService, type TimelineState } from '../timeline/timeline-service.js'
import {
  dateFromTime,
  generateDateCandidates,
  tileBoundsToLatLng,
} from '../../lib/snow-depth-utils.js'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'

const log = logger.create('terrain')

const PROTOCOL = 'snowdepth'
const TILE_SIZE = 256
const WORKER_POOL_SIZE = 4
const DEFAULT_OPACITY = 0.7
const MAX_COG_CACHE = 8
const MIN_ZOOM = 5
const MAX_ZOOM = 14

const COG_ORIGIN = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''
const COG_BASE_URL = `${COG_ORIGIN}/data/snodas`

// Double-buffer slots for seamless date switching
const BUFFERS = [
  { sourceId: 'snow-depth-source-a', layerId: 'snow-depth-layer-a' },
  { sourceId: 'snow-depth-source-b', layerId: 'snow-depth-layer-b' },
] as const

interface CogCacheEntry {
  tiff: GeoTIFF
  images: Map<number, GeoTIFFImage>
}

class SnowDepthService {
  private workers: Worker[] = []
  private workerIndex = 0
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY
  private pendingRequests = new Map<string, AbortController>()
  private nextRequestId = 0
  private cogCache = new Map<string, CogCacheEntry>()
  private _selectedDate: string = ''
  private _availableDates: string[] = []
  private dateVersion = 0

  // Double-buffer state
  private frontBuffer = 0
  private backBufferLoading = false
  private refreshGeneration = 0
  private sourceLoadHandler: ((e: { sourceId: string }) => void) | null = null

  get enabled(): boolean {
    return this._enabled
  }
  get opacity(): number {
    return this._opacity
  }
  get selectedDate(): string {
    return this._selectedDate
  }
  get availableDates(): string[] {
    return this._availableDates
  }

  private onTimelineChange = (state: Readonly<TimelineState>): void => {
    const dateStr = this.dateFromTime(state.currentTime)
    if (dateStr && dateStr !== this._selectedDate && this._availableDates.includes(dateStr)) {
      this._selectedDate = dateStr
      this.refreshTiles()
    }
  }

  private dateFromTime(time: Date): string {
    return dateFromTime(time, this._availableDates)
  }

  private initWorkers(): void {
    if (this.workers.length > 0) {
      return
    }
    for (let i = 0; i < WORKER_POOL_SIZE; i++) {
      this.workers.push(new SnowDepthWorker())
    }
  }

  private getWorker(): Worker {
    const worker = this.workers[this.workerIndex]
    this.workerIndex = (this.workerIndex + 1) % this.workers.length
    return worker
  }

  private terminateWorkers(): void {
    this.workers.forEach(w => w.terminate())
    this.workers = []
    this.workerIndex = 0
  }

  register(map: maplibregl.Map): void {
    this.map = map
    addProtocol(PROTOCOL, (params, abortController) => {
      return this.handleTileRequest(params, abortController)
    })
    this.discoverDates()
    timelineService.addListener(this.onTimelineChange)
  }

  unregister(): void {
    this.disable()
    timelineService.removeListener(this.onTimelineChange)
    try {
      removeProtocol(PROTOCOL)
    } catch (_) {
      /* may not be registered */
    }
    this.terminateWorkers()
    this.cogCache.clear()
    this.map = null
  }

  enable(): void {
    if (!this.map || this._enabled) {
      return
    }
    this.initWorkers()
    this._enabled = true
    this.frontBuffer = 0
    this.backBufferLoading = false

    this.addBuffer(0, 0)
    this.awaitBufferLoaded(0, () => {
      if (!this._enabled || !this.map) return
      const { layerId } = BUFFERS[0]
      if (this.map.getLayer(layerId)) {
        this.map.setPaintProperty(layerId, 'raster-opacity', this._opacity)
      }
    })
  }

  disable(): void {
    if (!this.map) {
      return
    }
    this._enabled = false

    if (this.sourceLoadHandler) {
      this.map.off('sourcedata', this.sourceLoadHandler)
      this.sourceLoadHandler = null
    }

    for (const [, controller] of this.pendingRequests) {
      controller.abort()
    }
    this.pendingRequests.clear()

    this.removeBuffer(0)
    this.removeBuffer(1)

    this.terminateWorkers()
  }

  toggle(): void {
    if (this._enabled) {
      this.disable()
    } else {
      this.enable()
    }
  }

  reAdd(): void {
    if (!this._enabled || !this.map) {
      return
    }
    this.initWorkers()
    const front = BUFFERS[this.frontBuffer]
    if (!this.map.getSource(front.sourceId)) {
      this.addBuffer(this.frontBuffer, 0)
      this.awaitBufferLoaded(this.frontBuffer, () => {
        if (!this._enabled || !this.map) return
        if (this.map.getLayer(front.layerId)) {
          this.map.setPaintProperty(front.layerId, 'raster-opacity', this._opacity)
        }
      })
    }
  }

  setOpacity(opacity: number): void {
    this._opacity = Math.max(0, Math.min(1, opacity))
    const front = BUFFERS[this.frontBuffer]
    if (this.map?.getLayer(front.layerId)) {
      this.map.setPaintProperty(front.layerId, 'raster-opacity', this._opacity)
    }
  }

  // ---- Double-buffer management ----

  private addBuffer(index: number, opacity: number): void {
    if (!this.map) {
      return
    }
    const buf = BUFFERS[index]

    if (!this.map.getSource(buf.sourceId)) {
      this.map.addSource(buf.sourceId, {
        type: 'raster',
        tiles: [`${PROTOCOL}://{z}/{x}/{y}?d=${this.dateVersion}`],
        tileSize: TILE_SIZE,
        minzoom: MIN_ZOOM,
        maxzoom: MAX_ZOOM,
        attribution: 'Snow Depth: NOAA SNODAS',
      })
    }

    if (!this.map.getLayer(buf.layerId)) {
      addOverlayLayer(
        this.map,
        {
          id: buf.layerId,
          type: 'raster',
          source: buf.sourceId,
          paint: {
            'raster-opacity': opacity,
            'raster-opacity-transition': { duration: 0, delay: 0 },
            'raster-fade-duration': 0,
            'raster-resampling': 'nearest',
          },
        },
        'snow-depth'
      )
    }
  }

  private removeBuffer(index: number): void {
    if (!this.map) {
      return
    }
    const buf = BUFFERS[index]
    if (this.map.getLayer(buf.layerId)) {
      this.map.removeLayer(buf.layerId)
    }
    if (this.map.getSource(buf.sourceId)) {
      this.map.removeSource(buf.sourceId)
    }
  }

  private refreshTiles(): void {
    if (!this.map || !this._enabled) {
      return
    }
    this.dateVersion++

    const backIndex = 1 - this.frontBuffer

    this.refreshGeneration++

    // Remove old back buffer, add new one invisibly
    this.removeBuffer(backIndex)
    this.addBuffer(backIndex, 0)
    this.backBufferLoading = true
    this.awaitBufferLoaded(backIndex, () => this.trySwap())
  }

  private awaitBufferLoaded(bufferIndex: number, onLoaded: () => void): void {
    if (!this.map) return
    if (this.sourceLoadHandler) {
      this.map.off('sourcedata', this.sourceLoadHandler)
      this.sourceLoadHandler = null
    }
    const sourceId = BUFFERS[bufferIndex].sourceId
    const handler = (e: { sourceId: string }): void => {
      if (e.sourceId !== sourceId || !this.map) return
      if (!this.map.isSourceLoaded(sourceId)) return
      this.map.off('sourcedata', handler)
      if (this.sourceLoadHandler === handler) this.sourceLoadHandler = null
      onLoaded()
    }
    this.sourceLoadHandler = handler
    this.map.on('sourcedata', handler)
  }

  private trySwap(): void {
    if (!this.backBufferLoading || !this.map || !this._enabled) {
      return
    }

    this.backBufferLoading = false

    const oldFront = this.frontBuffer
    const backIndex = 1 - oldFront
    const back = BUFFERS[backIndex]

    // Show back buffer, then defer removal of old front until MapLibre has
    // painted a frame with the new buffer visible, preventing a flash.
    if (this.map.getLayer(back.layerId)) {
      this.map.setPaintProperty(back.layerId, 'raster-opacity', this._opacity)
    }
    this.frontBuffer = backIndex

    const gen = this.refreshGeneration
    this.map.once('render', () => {
      if (gen !== this.refreshGeneration) return
      this.removeBuffer(oldFront)
    })
  }

  // ---- Date discovery ----

  private discoverDates(): void {
    this._availableDates = this.generateDateCandidates().sort()
    if (this._availableDates.length > 0 && !this._selectedDate) {
      this._selectedDate = this.dateFromTime(timelineService.getState().currentTime)
    }
    if (this._enabled) {
      if (this._selectedDate) {
        this.refreshTiles()
      }
    }
  }

  private generateDateCandidates(): string[] {
    return generateDateCandidates()
  }

  // ---- COG management ----

  private getCogFilename(): string {
    return `snodas_depth_${this._selectedDate}`
  }

  private async getCogEntry(filename: string): Promise<CogCacheEntry> {
    const cached = this.cogCache.get(filename)
    if (cached) {
      return cached
    }

    if (this.cogCache.size >= MAX_COG_CACHE) {
      const firstKey = this.cogCache.keys().next().value!
      this.cogCache.delete(firstKey)
    }

    const url = `${COG_BASE_URL}/${filename}.tif`
    const tiff = await fromUrl(url)
    const entry: CogCacheEntry = { tiff, images: new Map() }
    this.cogCache.set(filename, entry)
    return entry
  }

  private async getImage(entry: CogCacheEntry, overviewIndex: number): Promise<GeoTIFFImage> {
    const cached = entry.images.get(overviewIndex)
    if (cached) {
      return cached
    }

    const image = await entry.tiff.getImage(overviewIndex)
    entry.images.set(overviewIndex, image)
    return image
  }

  private tileBoundsToLatLng(
    z: number,
    x: number,
    y: number
  ): { west: number; south: number; east: number; north: number } {
    return tileBoundsToLatLng(z, x, y)
  }

  // ---- Tile request handler ----

  private async handleTileRequest(
    params: { url: string },
    abortController: AbortController
  ): Promise<{ data: ArrayBuffer }> {
    const match = params.url.match(/(\d+)\/(\d+)\/(\d+)/)
    if (!match) {
      throw new Error(`Invalid snow depth tile URL: ${params.url}`)
    }

    const z = parseInt(match[1])
    const x = parseInt(match[2])
    const y = parseInt(match[3])
    const key = `${z}/${x}/${y}`

    if (z < MIN_ZOOM || !this._selectedDate) {
      return { data: this.transparentTile() }
    }

    this.pendingRequests.set(key, abortController)

    try {
      const bounds = this.tileBoundsToLatLng(z, x, y)

      // SNODAS only covers CONUS
      if (
        bounds.east < -124.74 ||
        bounds.west > -66.94 ||
        bounds.north < 24.95 ||
        bounds.south > 52.88
      ) {
        return { data: this.transparentTile() }
      }

      const cogFilename = this.getCogFilename()

      if (abortController.signal.aborted) {
        throw new DOMException('Aborted', 'AbortError')
      }

      const entry = await this.getCogEntry(cogFilename)
      const image = await this.getImage(entry, 0)

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- signal state can change across awaits
      if (abortController.signal.aborted) {
        throw new DOMException('Aborted', 'AbortError')
      }

      const bbox = image.getBoundingBox()
      const imgWidth = image.getWidth()
      const imgHeight = image.getHeight()

      const pxPerDegX = imgWidth / (bbox[2] - bbox[0])
      const pxPerDegY = imgHeight / (bbox[3] - bbox[1])

      const pixelLeft = Math.floor((bounds.west - bbox[0]) * pxPerDegX)
      const pixelRight = Math.ceil((bounds.east - bbox[0]) * pxPerDegX)
      const pixelTop = Math.floor((bbox[3] - bounds.north) * pxPerDegY)
      const pixelBottom = Math.ceil((bbox[3] - bounds.south) * pxPerDegY)

      const winLeft = Math.max(0, pixelLeft)
      const winTop = Math.max(0, pixelTop)
      const winRight = Math.min(imgWidth, pixelRight)
      const winBottom = Math.min(imgHeight, pixelBottom)

      const winWidth = winRight - winLeft
      const winHeight = winBottom - winTop

      if (winWidth <= 0 || winHeight <= 0) {
        return { data: this.transparentTile() }
      }

      const rasters = await image.readRasters({
        window: [winLeft, winTop, winRight, winBottom],
        interleave: false,
      })

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- signal state can change across awaits
      if (abortController.signal.aborted) {
        throw new DOMException('Aborted', 'AbortError')
      }

      const rawValues = rasters[0] as unknown as Int16Array

      // Map each output pixel to the native SNODAS pixel using the same
      // geographic Math.floor formula as queryDepthAtPoint, so the rendered
      // blocks align exactly with the native pixel grid.
      const depthPixels = new Int16Array(TILE_SIZE * TILE_SIZE)
      const lonRange = bbox[2] - bbox[0]
      const latRange = bbox[3] - bbox[1]
      for (let oy = 0; oy < TILE_SIZE; oy++) {
        const lat = bounds.north - ((oy + 0.5) / TILE_SIZE) * (bounds.north - bounds.south)
        const nativeY = Math.floor(((bbox[3] - lat) / latRange) * imgHeight)
        const relY = nativeY - winTop
        if (relY < 0 || relY >= winHeight) continue
        const rowOff = oy * TILE_SIZE
        const srcRowOff = relY * winWidth
        for (let ox = 0; ox < TILE_SIZE; ox++) {
          const lng = bounds.west + ((ox + 0.5) / TILE_SIZE) * (bounds.east - bounds.west)
          const nativeX = Math.floor(((lng - bbox[0]) / lonRange) * imgWidth)
          const relX = nativeX - winLeft
          if (relX < 0 || relX >= winWidth) continue
          depthPixels[rowOff + ox] = rawValues[srcRowOff + relX]
        }
      }

      const coloredPixels = await this.processInWorker(depthPixels, abortController.signal)

      const canvas = new OffscreenCanvas(TILE_SIZE, TILE_SIZE)
      const ctx = canvas.getContext('2d')!
      const imageData = new ImageData(
        new Uint8ClampedArray(coloredPixels.buffer as ArrayBuffer),
        TILE_SIZE,
        TILE_SIZE
      )
      ctx.putImageData(imageData, 0, 0)

      const blob = await canvas.convertToBlob({ type: 'image/png' })
      const arrayBuffer = await blob.arrayBuffer()

      return { data: arrayBuffer }
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        throw e
      }
      log.warn(`Snow depth tile error for ${key}:`, e instanceof Error ? e.message : String(e))
      return { data: this.transparentTile() }
    } finally {
      this.pendingRequests.delete(key)
    }
  }

  private processInWorker(
    depthPixels: Int16Array,
    signal: AbortSignal
  ): Promise<Uint8ClampedArray> {
    return new Promise((resolve, reject) => {
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'))
        return
      }

      const requestId = this.nextRequestId++
      const worker = this.getWorker()
      const handler = (e: MessageEvent): void => {
        if (e.data.requestId !== requestId) {
          return
        }
        worker.removeEventListener('message', handler)
        signal.removeEventListener('abort', onAbort)
        resolve(new Uint8ClampedArray(e.data.pixels))
      }
      const onAbort = (): void => {
        worker.removeEventListener('message', handler)
        reject(new DOMException('Aborted', 'AbortError'))
      }

      worker.addEventListener('message', handler)
      signal.addEventListener('abort', onAbort)
      worker.postMessage({ depthPixels, requestId }, [depthPixels.buffer])
    })
  }

  private transparentTile(): ArrayBuffer {
    const png = new Uint8Array([
      0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a, 0x00, 0x00, 0x00, 0x0d, 0x49, 0x48, 0x44,
      0x52, 0x00, 0x00, 0x00, 0x01, 0x00, 0x00, 0x00, 0x01, 0x08, 0x06, 0x00, 0x00, 0x00, 0x1f,
      0x15, 0xc4, 0x89, 0x00, 0x00, 0x00, 0x0a, 0x49, 0x44, 0x41, 0x54, 0x78, 0x9c, 0x62, 0x00,
      0x00, 0x00, 0x02, 0x00, 0x01, 0xe5, 0x27, 0xde, 0xfc, 0x00, 0x00, 0x00, 0x00, 0x49, 0x45,
      0x4e, 0x44, 0xae, 0x42, 0x60, 0x82,
    ])
    return png.buffer
  }

  // ---- Grid query API (for route planner) ----

  async queryDepthInBbox(
    bounds: [number, number, number, number], // [west, south, east, north]
    outputWidth: number,
    outputHeight: number
  ): Promise<Float32Array | null> {
    if (!this._selectedDate) return null

    const [west, south, east, north] = bounds
    // SNODAS only covers CONUS
    if (west > -66.94 || east < -124.74 || south > 52.88 || north < 24.95) return null

    try {
      const cogFilename = this.getCogFilename()
      const entry = await this.getCogEntry(cogFilename)
      const image = await this.getImage(entry, 0)

      const bbox = image.getBoundingBox()
      const imgWidth = image.getWidth()
      const imgHeight = image.getHeight()

      const pxPerDegX = imgWidth / (bbox[2] - bbox[0])
      const pxPerDegY = imgHeight / (bbox[3] - bbox[1])

      const clampedWest = Math.max(west, bbox[0])
      const clampedEast = Math.min(east, bbox[2])
      const clampedSouth = Math.max(south, bbox[1])
      const clampedNorth = Math.min(north, bbox[3])

      if (clampedWest >= clampedEast || clampedSouth >= clampedNorth) return null

      const winLeft = Math.max(0, Math.floor((clampedWest - bbox[0]) * pxPerDegX))
      const winRight = Math.min(imgWidth, Math.ceil((clampedEast - bbox[0]) * pxPerDegX))
      const winTop = Math.max(0, Math.floor((bbox[3] - clampedNorth) * pxPerDegY))
      const winBottom = Math.min(imgHeight, Math.ceil((bbox[3] - clampedSouth) * pxPerDegY))

      if (winRight <= winLeft || winBottom <= winTop) return null

      const rasters = await image.readRasters({
        window: [winLeft, winTop, winRight, winBottom],
        width: outputWidth,
        height: outputHeight,
        interleave: false,
      })

      const rawPixels = rasters[0] as unknown as ArrayLike<number>
      const output = new Float32Array(outputWidth * outputHeight)
      for (let i = 0; i < output.length; i++) {
        const v = rawPixels[i]
        output[i] = v === -9999 || v <= 0 ? 0 : v
      }
      return output
    } catch (err) {
      log.warn('SNODAS grid query failed:', err)
      return null
    }
  }

  // ---- Point query API ----

  async queryDepthAtPoint(lat: number, lng: number): Promise<number | null> {
    if (!this._selectedDate) return null
    if (lng < -124.74 || lng > -66.94 || lat < 24.95 || lat > 52.88) return null

    try {
      const cogFilename = this.getCogFilename()
      const entry = await this.getCogEntry(cogFilename)
      const image = await this.getImage(entry, 0)

      const bbox = image.getBoundingBox()
      const imgWidth = image.getWidth()
      const imgHeight = image.getHeight()

      const px = Math.floor(((lng - bbox[0]) / (bbox[2] - bbox[0])) * imgWidth)
      const py = Math.floor(((bbox[3] - lat) / (bbox[3] - bbox[1])) * imgHeight)

      if (px < 0 || px >= imgWidth || py < 0 || py >= imgHeight) return null

      const rasters = await image.readRasters({
        window: [px, py, px + 1, py + 1],
        width: 1,
        height: 1,
        interleave: false,
      })

      const value = (rasters[0] as unknown as number[])[0]
      if (value === -9999 || value <= 0) return null
      return value
    } catch (err) {
      log.warn('SNODAS point query failed:', err)
      return null
    }
  }
}

export const snowDepthService = new SnowDepthService()
